import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faLock, faUnlock, faSpinner, faCheckCircle,
  faTimesCircle, faKey, faMobileAlt, faEnvelope, faExclamationTriangle,
  faCopy, faDownload, faRedo
} from '@fortawesome/free-solid-svg-icons';
import { userAPI } from '../services/api';
import './TwoFactorAuth.css';

const TwoFactorAuth = ({ isDarkMode }) => {
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Enable 2FA flow
  const [showEnableFlow, setShowEnableFlow] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');

  // Disable 2FA flow
  const [showDisableFlow, setShowDisableFlow] = useState(false);
  const [password, setPassword] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showingBackupCodes, setShowingBackupCodes] = useState(false);

  // Fetch 2FA status on component mount
  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await userAPI.get2FAStatus();
      if (response && response.data) {
        setIs2FAEnabled(response.data.enabled || false);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    try {
      setProcessing(true);
      setError('');
      setSuccess('');

      const response = await userAPI.enable2FA();

      if (response && response.success) {
        setOtpSent(true);
        setSuccess('OTP sent to your registered phone/email. Please enter it below.');
      } else {
        setError(response?.message || 'Failed to enable 2FA. Please try again.');
      }
    } catch (error) {
      setError(error.message || 'Failed to enable 2FA. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      setProcessing(true);
      setError('');

      const response = await userAPI.verify2FA(otp);

      if (response && response.success) {
        setSuccess('2FA enabled successfully! Your account is now more secure.');
        setIs2FAEnabled(true);
        setShowEnableFlow(false);
        setOtpSent(false);
        setOtp('');

        // Auto-hide success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response?.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      setError(error.message || 'Invalid OTP. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();

    if (!password) {
      setError('Please enter your password');
      return;
    }

    try {
      setProcessing(true);
      setError('');

      const response = await userAPI.disable2FA(password);

      if (response && response.success) {
        setSuccess('2FA disabled successfully.');
        setIs2FAEnabled(false);
        setShowDisableFlow(false);
        setPassword('');
        setBackupCodes([]);
        setShowingBackupCodes(false);

        // Auto-hide success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response?.message || 'Invalid password. Please try again.');
      }
    } catch (error) {
      setError(error.message || 'Invalid password. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleGenerateBackupCodes = async () => {
    try {
      setProcessing(true);
      setError('');

      const response = await userAPI.generateBackupCodes();

      if (response && response.success) {
        setBackupCodes(response.data.codes || []);
        setShowingBackupCodes(true);
        setSuccess('New backup codes generated successfully. Please save them in a secure place.');
      } else {
        setError(response?.message || 'Failed to generate backup codes.');
      }
    } catch (error) {
      setError(error.message || 'Error generating backup codes.');
    } finally {
      setProcessing(false);
    }
  };

  const copyCodesToClipboard = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText)
      .then(() => {
        const originalSuccess = success;
        setSuccess('Backup codes copied to clipboard!');
        setTimeout(() => setSuccess(originalSuccess), 2000);
      })
      .catch(() => setError('Failed to copy codes.'));
  };

  const downloadCodes = () => {
    const content = `AutoboyExpress Backup Codes\nGenerated at: ${new Date().toLocaleString()}\n\n${backupCodes.join('\n')}\n\nKeep these codes in a safe place. Each code can only be used once.`;
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'autoboy_backup_codes.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const cancelEnableFlow = () => {
    setShowEnableFlow(false);
    setOtpSent(false);
    setOtp('');
    setError('');
    setSuccess('');
  };

  const cancelDisableFlow = () => {
    setShowDisableFlow(false);
    setPassword('');
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <div className={`twofa-container ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="twofa-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Loading 2FA settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`twofa-container ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Header */}
      <div className="twofa-header">
        <div className="twofa-header-icon">
          <FontAwesomeIcon icon={faShieldAlt} />
        </div>
        <div className="twofa-header-content">
          <h2>Two-Factor Authentication</h2>
          <p>Add an extra layer of security to your account</p>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`twofa-status ${is2FAEnabled ? 'enabled' : 'disabled'}`}>
        <FontAwesomeIcon icon={is2FAEnabled ? faLock : faUnlock} />
        <span>
          2FA is currently <strong>{is2FAEnabled ? 'ENABLED' : 'DISABLED'}</strong>
        </span>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="twofa-alert twofa-alert-success">
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="twofa-alert twofa-alert-error">
          <FontAwesomeIcon icon={faTimesCircle} />
          <span>{error}</span>
        </div>
      )}

      {/* Main Content */}
      <div className="twofa-content">
        {!is2FAEnabled ? (
          // Enable 2FA Section
          <div className="twofa-section">
            <div className="twofa-info">
              <h3>Why enable 2FA?</h3>
              <ul>
                <li>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>Protect your account from unauthorized access</span>
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>Secure your wallet and transactions</span>
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>Receive OTP via SMS or Email</span>
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>Industry-standard security practice</span>
                </li>
              </ul>
            </div>

            {!showEnableFlow ? (
              <button
                className="twofa-btn twofa-btn-primary"
                onClick={() => setShowEnableFlow(true)}
              >
                <FontAwesomeIcon icon={faShieldAlt} />
                Enable Two-Factor Authentication
              </button>
            ) : (
              <div className="twofa-form-container">
                {!otpSent ? (
                  // Step 1: Initiate 2FA
                  <div className="twofa-step">
                    <div className="twofa-step-header">
                      <FontAwesomeIcon icon={faMobileAlt} />
                      <h4>Step 1: Request OTP</h4>
                    </div>
                    <p>Click the button below to receive a one-time password (OTP) on your registered phone number or email address.</p>
                    <div className="twofa-form-actions">
                      <button
                        className="twofa-btn twofa-btn-secondary"
                        onClick={cancelEnableFlow}
                        disabled={processing}
                      >
                        Cancel
                      </button>
                      <button
                        className="twofa-btn twofa-btn-primary"
                        onClick={handleEnable2FA}
                        disabled={processing}
                      >
                        {processing ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Sending OTP...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faEnvelope} />
                            Send OTP
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  // Step 2: Verify OTP
                  <form onSubmit={handleVerify2FA} className="twofa-step">
                    <div className="twofa-step-header">
                      <FontAwesomeIcon icon={faKey} />
                      <h4>Step 2: Verify OTP</h4>
                    </div>
                    <p>Enter the 6-digit code sent to your phone/email.</p>
                    <div className="twofa-form-group">
                      <label htmlFor="otp">One-Time Password (OTP)</label>
                      <input
                        type="text"
                        id="otp"
                        placeholder="Enter 6-digit OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        maxLength="6"
                        autoComplete="off"
                        required
                      />
                      <small>Code expires in 10 minutes</small>
                    </div>
                    <div className="twofa-form-actions">
                      <button
                        type="button"
                        className="twofa-btn twofa-btn-secondary"
                        onClick={cancelEnableFlow}
                        disabled={processing}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="twofa-btn twofa-btn-primary"
                        disabled={processing || otp.length !== 6}
                      >
                        {processing ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Verifying...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faCheckCircle} />
                            Verify & Enable 2FA
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        ) : (
          // Disable 2FA Section
          <div className="twofa-section">
            <div className="twofa-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <div>
                <h4>2FA is currently enabled</h4>
                <p>Disabling 2FA will make your account less secure. We recommend keeping it enabled.</p>
              </div>
            </div>

            {!showDisableFlow ? (
              <button
                className="twofa-btn twofa-btn-danger"
                onClick={() => setShowDisableFlow(true)}
              >
                <FontAwesomeIcon icon={faUnlock} />
                Disable Two-Factor Authentication
              </button>
            ) : (
              <form onSubmit={handleDisable2FA} className="twofa-form-container">
                <div className="twofa-step">
                  <div className="twofa-step-header">
                    <FontAwesomeIcon icon={faLock} />
                    <h4>Confirm Password</h4>
                  </div>
                  <p>Enter your account password to disable 2FA.</p>
                  <div className="twofa-form-group">
                    <label htmlFor="password">Password</label>
                    <input
                      type="password"
                      id="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </div>
                  <div className="twofa-form-actions">
                    <button
                      type="button"
                      className="twofa-btn twofa-btn-secondary"
                      onClick={cancelDisableFlow}
                      disabled={processing}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="twofa-btn twofa-btn-danger"
                      disabled={processing}
                    >
                      {processing ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin />
                          Disabling...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faUnlock} />
                          Disable 2FA
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Backup Codes Section */}
        {is2FAEnabled && !showDisableFlow && (
          <div className="twofa-section backup-codes-section">
            <div className="twofa-section-header">
              <FontAwesomeIcon icon={faLock} />
              <h3>Backup Codes</h3>
            </div>
            <p className="backup-codes-info">
              Backup codes can be used to access your account if you lose access to your primary 2FA method.
              Each code can only be used once.
            </p>

            {showingBackupCodes && backupCodes.length > 0 ? (
              <div className="backup-codes-display">
                <div className="backup-codes-grid">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="backup-code-item">
                      <code>{code}</code>
                    </div>
                  ))}
                </div>
                <div className="backup-codes-actions">
                  <button className="twofa-btn twofa-btn-secondary" onClick={copyCodesToClipboard}>
                    <FontAwesomeIcon icon={faCopy} /> Copy
                  </button>
                  <button className="twofa-btn twofa-btn-secondary" onClick={downloadCodes}>
                    <FontAwesomeIcon icon={faDownload} /> Download
                  </button>
                  <button className="twofa-btn twofa-btn-secondary" onClick={() => setShowingBackupCodes(false)}>
                    Hide
                  </button>
                </div>
                <div className="backup-codes-warning">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>Important: These codes will not be shown again. Save them securely now.</span>
                </div>
              </div>
            ) : (
              <button
                className="twofa-btn twofa-btn-secondary"
                onClick={handleGenerateBackupCodes}
                disabled={processing}
              >
                {processing ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Generating...</>
                ) : (
                  <><FontAwesomeIcon icon={faRedo} /> Generate New Backup Codes</>
                )}
              </button>
            )}
          </div>
        )}

        {/* Security Tips */}
        <div className="twofa-tips">
          <h4>Security Tips</h4>
          <ul>
            <li>Keep your password strong and unique</li>
            <li>Never share your OTP with anyone</li>
            <li>Enable 2FA on all important accounts</li>
            <li>Use a secure email and phone number</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuth;
